import { ConvexAuthProvider } from "@convex-dev/auth/react";
import { ConvexQueryClient } from "@convex-dev/react-query";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConvexReactClient } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@/lib/convex-client";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { useAuth } from "../../hooks/useAuth";
import type { Organization } from "../../types/admin";

export type ActiveOrganizationSettings = FunctionReturnType<typeof api.settings.getForOrganization>;

interface UserDataState {
  userProfile: FunctionReturnType<typeof api.users.getCurrentUser> | undefined;
  isLoading: boolean;
}

interface UserOrgState {
  userOrganization: Organization | undefined | null;
  isLoading: boolean;
  isResolved: boolean;
}

interface OrganizationsState {
  organizations: Array<Organization> | undefined;
  organizationsById: Map<Id<"organizations">, Organization> | undefined;
  isLoading: boolean;
  isResolved: boolean;
  getOrganizationById: (id: Id<"organizations">) => Organization | undefined;
}

interface ActiveOrgState {
  // `undefined` while we are still resolving auth + org queries. Consumers
  // that render different UI for "no org selected" vs "still loading"
  // should distinguish `undefined` (loading) from `null` (resolved, no
  // org). Most consumers just do `activeOrganization?._id` which is
  // safe for all three shapes.
  activeOrganization: Organization | null | undefined;
  setActiveOrganization: (organization: Organization | null) => void;
  organizations: Array<Organization> | undefined;
  isLoading: boolean;
  isResolved: boolean;
}

interface ActiveOrgSettingsState {
  settings: ActiveOrganizationSettings | undefined;
}

const UserDataContext = createContext<UserDataState | null>(null);
const UserOrgContext = createContext<UserOrgState | null>(null);
const OrganizationsContext = createContext<OrganizationsState | null>(null);
const ActiveOrgContext = createContext<ActiveOrgState | null>(null);
const ActiveOrgSettingsContext = createContext<ActiveOrgSettingsState | null>(null);

const convexUrl = import.meta.env.VITE_CONVEX_URL;

if (!convexUrl) {
  throw new Error("VITE_CONVEX_URL is not set. Please add it to your environment configuration.");
}

const convexClient = new ConvexReactClient(convexUrl);
const convexQueryClient = new ConvexQueryClient(convexClient);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryKeyHashFn: convexQueryClient.hashFn(),
      queryFn: convexQueryClient.queryFn(),
    },
  },
});

convexQueryClient.connect(queryClient);

interface AppDataProviderProps {
  children: React.ReactNode;
}

export function AppDataProvider({ children }: AppDataProviderProps) {
  return (
    <ConvexAuthProvider client={convexClient}>
      <QueryClientProvider client={queryClient}>
        <AppDataProviderImpl>{children}</AppDataProviderImpl>
      </QueryClientProvider>
    </ConvexAuthProvider>
  );
}

function AppDataProviderImpl({ children }: AppDataProviderProps) {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const updateLastVisitedOrganization = useMutation(api.users.updateLastVisitedOrganization);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<Id<"organizations"> | null>(
    null,
  );

  const skipQueries = !isAuthenticated || !user;
  const isSuperadmin = user?.role === "superadmin";

  const userOrganizationArgs = useMemo(
    () => (skipQueries || !user?.organization ? "skip" : { organizationId: user.organization }),
    [skipQueries, user?.organization],
  );
  const userOrganization = useQuery(api.organizations.getById, userOrganizationArgs);

  const organizationsArgs = useMemo(
    () => (skipQueries || !isSuperadmin ? "skip" : {}),
    [skipQueries, isSuperadmin],
  );
  const organizationsRaw = useQuery(api.organizations.list, organizationsArgs);

  const organizations = useMemo(() => {
    if (isSuperadmin) {
      return organizationsRaw;
    }
    if (userOrganization) {
      return [userOrganization];
    }
    if (!user?.organization) {
      return [];
    }
    return undefined;
  }, [isSuperadmin, organizationsRaw, user?.organization, userOrganization]);

  const resolvedSelectedOrganizationId = useMemo(() => {
    if (!isSuperadmin) {
      return user?.organization ?? null;
    }
    if (!organizationsRaw || organizationsRaw.length === 0) {
      return null;
    }

    const organizationIds = new Set(organizationsRaw.map((organization) => organization._id));
    if (selectedOrganizationId && organizationIds.has(selectedOrganizationId)) {
      return selectedOrganizationId;
    }
    if (user?.lastVisitedOrganization && organizationIds.has(user.lastVisitedOrganization)) {
      return user.lastVisitedOrganization;
    }
    return organizationsRaw[0]?._id ?? null;
  }, [
    isSuperadmin,
    organizationsRaw,
    selectedOrganizationId,
    user?.lastVisitedOrganization,
    user?.organization,
  ]);

  useEffect(() => {
    if (selectedOrganizationId === resolvedSelectedOrganizationId) {
      return;
    }
    setSelectedOrganizationId(resolvedSelectedOrganizationId);
  }, [resolvedSelectedOrganizationId, selectedOrganizationId]);

  const activeOrganization: Organization | null | undefined = useMemo(() => {
    // We only "commit" to a resolved value once loading is complete. While
    // loading we emit `undefined` (pending) rather than `null`, which
    // previously caused the "Select an organization" UI to flash on page
    // load in the window between auth resolving and the org query finishing.
    if (skipQueries) {
      return null;
    }
    if (!isSuperadmin) {
      if (user?.organization && userOrganization === undefined) {
        return undefined;
      }
      return userOrganization ?? null;
    }
    // Superadmin path: wait until we have the org list AND a resolved id.
    if (organizationsRaw === undefined) {
      return undefined;
    }
    if (!resolvedSelectedOrganizationId) {
      // List resolved but nothing to select: a real "no org" resolution.
      return organizationsRaw.length === 0 ? null : undefined;
    }
    return (
      organizationsRaw.find(
        (organization) => organization._id === resolvedSelectedOrganizationId,
      ) ?? null
    );
  }, [
    isSuperadmin,
    organizationsRaw,
    resolvedSelectedOrganizationId,
    skipQueries,
    user?.organization,
    userOrganization,
  ]);

  const organizationsById = useMemo(() => {
    if (!organizations) {
      return undefined;
    }
    return new Map(organizations.map((organization) => [organization._id, organization]));
  }, [organizations]);

  const getOrganizationById = useCallback(
    (id: Id<"organizations">) => organizationsById?.get(id),
    [organizationsById],
  );

  const setActiveOrganizationStable = useCallback(
    (organization: Organization | null) => {
      const organizationId = organization?._id ?? null;
      setSelectedOrganizationId(organizationId);

      if (!isSuperadmin) {
        return;
      }

      void updateLastVisitedOrganization({ organizationId: organizationId ?? undefined }).catch(
        (error) => {
          console.error("Failed to persist active organization", error);
        },
      );
    },
    [isSuperadmin, updateLastVisitedOrganization],
  );

  const isUserOrganizationLoading = Boolean(userOrganization === undefined && user?.organization);
  const isUserOrganizationResolved = Boolean(
    skipQueries || !user?.organization || userOrganization !== undefined,
  );
  const isOrganizationsResolved = isSuperadmin
    ? Boolean(skipQueries || organizationsRaw !== undefined)
    : isUserOrganizationResolved;
  const isOrganizationsLoading = !isOrganizationsResolved;
  const isActiveOrganizationLoading = isSuperadmin
    ? Boolean(
        isOrganizationsLoading ||
          (!resolvedSelectedOrganizationId && (organizationsRaw?.length ?? 0) > 0),
      )
    : isUserOrganizationLoading;

  const userDataValue = useMemo<UserDataState>(
    () => ({
      userProfile: user,
      isLoading: isAuthLoading,
    }),
    [user, isAuthLoading],
  );

  const userOrgValue = useMemo<UserOrgState>(
    () => ({
      userOrganization,
      isLoading: isUserOrganizationLoading,
      isResolved: isUserOrganizationResolved,
    }),
    [userOrganization, isUserOrganizationLoading, isUserOrganizationResolved],
  );

  const organizationsValue = useMemo<OrganizationsState>(
    () => ({
      organizations,
      organizationsById,
      isLoading: isOrganizationsLoading,
      isResolved: isOrganizationsResolved,
      getOrganizationById,
    }),
    [
      organizations,
      organizationsById,
      isOrganizationsLoading,
      isOrganizationsResolved,
      getOrganizationById,
    ],
  );

  const activeOrgValue = useMemo<ActiveOrgState>(
    () => ({
      activeOrganization,
      setActiveOrganization: setActiveOrganizationStable,
      organizations,
      isLoading: isActiveOrganizationLoading,
      isResolved: isOrganizationsResolved && !isActiveOrganizationLoading,
    }),
    [
      activeOrganization,
      setActiveOrganizationStable,
      organizations,
      isActiveOrganizationLoading,
      isOrganizationsResolved,
    ],
  );

  // Org settings are shared across many surfaces (calendar sheet, next-
  // meeting pill, admin panels). Subscribing here dedupes the subscription
  // and gives consumers an instant value when they mount. Memoize the args
  // so the query key is stable between parent renders.
  const activeOrganizationId = activeOrganization?._id ?? null;
  const settingsArgs = useMemo(
    () =>
      skipQueries || !activeOrganizationId
        ? ("skip" as const)
        : { organizationId: activeOrganizationId },
    [skipQueries, activeOrganizationId],
  );
  const activeOrgSettings = useQuery(api.settings.getForOrganization, settingsArgs);

  const activeOrgSettingsValue = useMemo<ActiveOrgSettingsState>(
    () => ({ settings: activeOrgSettings }),
    [activeOrgSettings],
  );

  return (
    <UserDataContext.Provider value={userDataValue}>
      <UserOrgContext.Provider value={userOrgValue}>
        <OrganizationsContext.Provider value={organizationsValue}>
          <ActiveOrgContext.Provider value={activeOrgValue}>
            <ActiveOrgSettingsContext.Provider value={activeOrgSettingsValue}>
              {children}
            </ActiveOrgSettingsContext.Provider>
          </ActiveOrgContext.Provider>
        </OrganizationsContext.Provider>
      </UserOrgContext.Provider>
    </UserDataContext.Provider>
  );
}

export function useUserProfile() {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error("useUserProfile must be used within AppDataProvider");
  }
  return context;
}

export function useUserOrganization() {
  const context = useContext(UserOrgContext);
  if (!context) {
    throw new Error("useUserOrganization must be used within AppDataProvider");
  }
  return context;
}

export function useOrganizations() {
  const context = useContext(OrganizationsContext);
  if (!context) {
    throw new Error("useOrganizations must be used within AppDataProvider");
  }
  return context;
}

export function useActiveOrganization() {
  const context = useContext(ActiveOrgContext);
  if (!context) {
    throw new Error("useActiveOrganization must be used within AppDataProvider");
  }
  return context;
}

// Subscribes to the active organization's settings (event types, etc.). A
// single subscription is shared across all surfaces that need it — calendar
// sheet, next-meeting pill, admin panels — so we do not spin up duplicate
// websocket subscriptions for the same payload.
export function useActiveOrganizationSettings() {
  const context = useContext(ActiveOrgSettingsContext);
  if (!context) {
    throw new Error("useActiveOrganizationSettings must be used within AppDataProvider");
  }
  return context;
}
